import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { consultarCategorias } from '../../../nucleo/api/nucleoApi.js';
import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { consultarCuentas, PROPOSITOS } from '../../cuentas/api/cuentasApi.js';
import { ComboboxDeCuenta } from '../../movimientos/componentes/ComboboxDeCuenta.jsx';
import { obtenerPrecargaDePago, registrarPago } from '../api/prestamosApi.js';
import { importe as formatearImporte } from '../formato.js';

/** Centavos enteros, igual que el servidor (RNF-033): comparar con decimales descuadra. */
function aCentavos(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.round(numero * 100) : 0;
}

/**
 * REGISTRAR UN PAGO DEL PRÉSTAMO (P-20-03, RF-267, D-430, D-435, D-443).
 *
 * EL PAGO SE PARTE EN DOS HECHOS REALES y en UNA SOLA operación (RN-115, RN-744e):
 * un TRASPASO del capital —que baja lo que debes— y un GASTO del interés —que entra
 * al mes con su categoría—. Si algo falla, no queda nada a medias.
 *
 * EL REPARTO LLEGA PUESTO Y LO DECIDE ELLA (D-430, RNF-133): la propuesta sale del
 * ÚLTIMO PAGO registrado y, en el primero, del PROMEDIO del contrato. Los DOS campos
 * son editables, conservan su valor, quedan SELECCIONADOS COMPLETOS al tomar el foco
 * y JAMÁS SE DESHABILITAN —el interés real de cada mes lo dice su banco—. El texto
 * que explica de dónde sale la cifra lo redacta el SERVIDOR (RNF-130), y cuando no
 * pudo salir de ningún sitio, lo dice con esas palabras (RN-745c).
 *
 * SIN PLAN DE PAGOS SE PUEDE REGISTRAR IGUAL (D-457, RN-778): la deuda no depende del
 * plan. Lo único que cambia es que no hay propuesta —todo lo escrito queda como
 * capital y el interés en cero—, y la pantalla avisa que dejar el interés en cero
 * deja ese mes sin el gasto de intereses.
 *
 * EL CUADRE SE VE ANTES DE INTENTAR GUARDAR y nunca se ajusta solo (RN-744b, D-443):
 * la comparación capital + interés contra lo pagado está permanente a la vista, y el
 * botón «Poner el interés en $X» corrige SOLO al pulsarlo. La validación de verdad
 * es del servidor, con su literal.
 */
export default function RegistrarPagoDePrestamo() {
  const { idCuenta } = useParams();
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const hoy = obtenerFechaDeNegocioHoy();

  // D-438 / D-414 · el DATO DE APERTURA cuando se llega desde lo que vence: el
  // pendiente del esperado precarga «Lo que pagaste», editable (RNF-133). Abierta
  // por otro camino, manda la precarga del servidor.
  const pendienteDeApertura = parametros.get('pendiente');
  const vieneDeVencen = parametros.get('desde') === 'vencen';

  const [precarga, setPrecarga] = useState(null);
  const [cuentasOrigen, setCuentasOrigen] = useState([]);
  const [cargandoCuentas, setCargandoCuentas] = useState(true);
  const [categorias, setCategorias] = useState([]);

  const [idCuentaOrigen, setIdCuentaOrigen] = useState(null);
  const [fecha, setFecha] = useState(hoy);
  const [importePagado, setImportePagado] = useState('');
  const [importeCapital, setImporteCapital] = useState('');
  const [importeInteres, setImporteInteres] = useState('');
  const [idCategoriaDelInteres, setIdCategoriaDelInteres] = useState('');
  const [nota, setNota] = useState('');

  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [laPrecarga, respuestaDeCuentas, respuestaDeCategorias] = await Promise.all([
          obtenerPrecargaDePago({ idCuenta: Number(idCuenta), fechaLocal: hoy }),
          consultarCuentas({ proposito: PROPOSITOS.capturarOrigen }),
          consultarCategorias({ tipo: 'GASTO', soloActivas: true }),
        ]);

        setPrecarga(laPrecarga);
        setCategorias(respuestaDeCategorias.categorias ?? respuestaDeCategorias);

        /*
         * S-20-01 · LA CUENTA DE ORIGEN TIENE QUE SER DE LA MISMA MONEDA: no hay
         * conversión y no la habrá. Se filtra para no ofrecer lo que el servidor
         * rechazaría, y el préstamo mismo se excluye: nadie se paga a sí mismo.
         */
        const propias = (respuestaDeCuentas.cuentas ?? [])
          .filter((c) => c.codigoISO === laPrecarga.codigoISO && c.idCuenta !== Number(idCuenta));
        setCuentasOrigen(propias);

        // Lo que el campo «Lo que pagaste» abre: el pendiente del esperado si se
        // llegó desde lo que vence, el pago mensual si se llegó del préstamo, o
        // vacío si no hay plan (RN-745c).
        setImportePagado(pendienteDeApertura ?? laPrecarga.importePagadoPropuesto ?? '');
        setImporteCapital(laPrecarga.importeCapitalPropuesto ?? '');
        setImporteInteres(laPrecarga.importeInteresPropuesto ?? '');
        setIdCategoriaDelInteres(
          laPrecarga.idCategoriaDelInteresPropuesta ? String(laPrecarga.idCategoriaDelInteresPropuesta) : '',
        );
      } catch (fallo) {
        setError(fallo);
      } finally {
        setCargandoCuentas(false);
      }
    })();
  }, [idCuenta, hoy, pendienteDeApertura]);

  const codigoISO = precarga?.codigoISO ?? null;

  const cuadre = useMemo(() => {
    const pagado = aCentavos(importePagado);
    const suma = aCentavos(importeCapital) + aCentavos(importeInteres);
    return { pagado, suma, diferencia: pagado - suma, cuadra: pagado === suma };
  }, [importePagado, importeCapital, importeInteres]);

  const interesQueCuadra = Math.max(0, cuadre.pagado - aCentavos(importeCapital)) / 100;
  const capitalSePasaDeLaDeuda = precarga
    && aCentavos(importeCapital) > aCentavos(precarga.deudaVigente);
  const faltaLaCategoria = aCentavos(importeInteres) > 0 && !idCategoriaDelInteres;
  const interesEnCero = importeInteres !== '' && aCentavos(importeInteres) === 0;

  const puedeGuardar = Boolean(idCuentaOrigen)
    && Boolean(fecha)
    && cuadre.pagado > 0
    && importeCapital !== ''
    && importeInteres !== ''
    && cuadre.cuadra
    && !capitalSePasaDeLaDeuda
    && !faltaLaCategoria;

  async function guardar() {
    if (!puedeGuardar) return;
    setError(null);
    setGuardando(true);
    try {
      await guardarConConexion(() => registrarPago({
        idCuenta: Number(idCuenta),
        idCuentaOrigen,
        fecha,
        importePagado: String(importePagado),
        importeCapital: String(importeCapital),
        importeInteres: String(importeInteres),
        idCategoriaDelInteres: idCategoriaDelInteres ? Number(idCategoriaDelInteres) : null,
        nota: nota.trim() || null,
        fechaLocal: hoy,
      }));

      // Se vuelve DE DONDE SE ABRIÓ, con su acuse (RN-396).
      navegar(
        vieneDeVencen ? '/cuentas/vencimientos' : `/cuentas/prestamos/${idCuenta}?acuse=pago`,
        { replace: true },
      );
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Registrar un pago" volverA={`/cuentas/prestamos/${idCuenta}`}>
      <CintaDeVersionNueva />

      <div className="aviso i" role="status" data-prueba="pago-introduccion">
        <b className="tit">Este pago se parte en dos</b>
        El capital baja lo que debes y el interés es gasto del mes, con su categoría. Los dos los
        decides tú: la aplicación te propone el reparto, pero el interés real de cada mes lo dice tu
        banco.
      </div>

      <AvisoDeError error={error} />

      {precarga && (
        <div className="card" style={{ margin: 12 }} data-prueba="pago-encabezado">
          <div className="fila">
            <span className="crece">{precarga.nombreDelPrestamo}</span>
            {/* RN-779 (b) · la IDENTIDAD de la mensualidad del periodo: 15/48. Sin
                plan no existe y no se enseña (RN-778). */}
            {precarga.contadorDeLaMensualidadDelPeriodo && (
              <b data-prueba="pago-contador">{precarga.contadorDeLaMensualidadDelPeriodo}</b>
            )}
          </div>
          <div className="fila">
            <span className="crece">Debes hoy</span>
            <b data-prueba="pago-deuda">{formatearImporte(precarga.deudaVigente, codigoISO)}</b>
          </div>
          {precarga.pendienteDelEsperado && (
            <div className="fila">
              <span className="crece">Falta del pago de este mes</span>
              <b>{formatearImporte(precarga.pendienteDelEsperado, codigoISO)}</b>
            </div>
          )}
        </div>
      )}

      {/* De dónde viene la cifra propuesta, o de dónde NO pudo salir. EL TEXTO ES
          DEL SERVIDOR (RN-745c, RN-778, RNF-130): la pantalla no lo redacta. */}
      {precarga?.textoDeLaPropuesta && (
        <div
          className={`aviso ${precarga.hayPropuesta ? 'i' : 'w'}`}
          role="status"
          data-prueba="pago-texto-de-la-propuesta"
        >
          {precarga.hayPropuesta ? '' : '⚠ '}
          {precarga.textoDeLaPropuesta}
          {!precarga.planGenerado && (
            <>
              {' '}
              <button
                className="btn sec"
                style={{ padding: '2px 8px', fontSize: 12 }}
                type="button"
                onClick={() => navegar(`/cuentas/prestamos/${idCuenta}/plan`)}
                data-prueba="pago-generar-plan"
              >
                Generar el plan de pagos
              </button>
            </>
          )}
        </div>
      )}

      <div className="card" style={{ margin: 12 }} data-prueba="pago-formulario">
        <div className="campo">
          {/* El rótulo lo pone el propio selector (RF-187, RN-562): rótulo y campo
              presentes desde el primer fotograma, con su texto de petición. */}
          <ComboboxDeCuenta
            id="idCuentaOrigen"
            etiqueta="¿DE QUÉ CUENTA SALE? — siempre una cuenta TUYA y activa"
            cuentas={cuentasOrigen}
            cargando={cargandoCuentas}
            valor={idCuentaOrigen}
            alElegir={(elegida) => setIdCuentaOrigen(elegida ?? null)}
            textoDePeticion="Elige la cuenta de la que sale el pago…"
            obligatorio
            ayuda={`Solo tus cuentas activas en ${codigoISO ?? 'la misma moneda'}: un pago no cambia de moneda.`}
            datoDePrueba="pago-campo-cuenta-origen"
          />
        </div>

        <div className="campo">
          <label htmlFor="fecha">FECHA DEL PAGO</label>
          <CampoDeFecha
            id="fecha"
            name="fecha"
            valor={fecha}
            alCambiar={setFecha}
            max={hoy}
            datoDePrueba="pago-campo-fecha"
          />
          <span className="ayuda">No puede ser posterior a hoy.</span>
        </div>

        <div className="campo">
          <label htmlFor="importePagado">LO QUE PAGASTE</label>
          {/* RNF-133 · el campo ÚNICO del núcleo, sin propiedades nuevas, nunca
              deshabilitado y seleccionado completo al tomar el foco. */}
          <CampoDeImporte
            id="importePagado"
            valor={importePagado}
            alCambiar={setImportePagado}
            codigoDivisa={codigoISO}
            contexto="lo que pagaste del préstamo"
            data-prueba="pago-campo-pagado"
          />
          <span className="ayuda">El cargo tal como aparece en tu cuenta.</span>
        </div>

        <div className="campo">
          <label htmlFor="importeCapital">CAPITAL</label>
          <CampoDeImporte
            id="importeCapital"
            valor={importeCapital}
            alCambiar={setImporteCapital}
            codigoDivisa={codigoISO}
            contexto="el capital del pago"
            data-prueba="pago-campo-capital"
          />
          <span className="ayuda">Es lo que baja tu deuda.</span>

          {/* D-443 · el literal es el del prototipo aprobado; la validación de verdad
              es del servidor, que rechaza sin escribir nada (CA-20-14). */}
          {capitalSePasaDeLaDeuda && (
            <div className="aviso e" role="status" data-prueba="pago-error-capital">
              El capital no puede ser mayor que lo que debes
              ({formatearImporte(precarga.deudaVigente, codigoISO)}). Si estás liquidando el préstamo,
              pon el capital exacto que debes.
            </div>
          )}
        </div>

        <div className="campo">
          <label htmlFor="importeInteres">INTERÉS</label>
          <CampoDeImporte
            id="importeInteres"
            valor={importeInteres}
            alCambiar={setImporteInteres}
            codigoDivisa={codigoISO}
            contexto="el interés del pago"
            data-prueba="pago-campo-interes"
          />
          <span className="ayuda">Entra como gasto de su mes, en la categoría que elijas.</span>

          {interesEnCero && (
            <div className="aviso w" role="status" data-prueba="pago-aviso-interes-en-cero">
              ⚠ Con el interés en cero, este mes no va a tener el gasto de intereses, aunque tu banco
              te lo haya cobrado. Si parte del pago fue interés, escríbelo aquí.
            </div>
          )}
        </div>

        {/* ===== EL CUADRE, PERMANENTE A LA VISTA (RN-744b) ================ */}
        <div className="fila" data-prueba="pago-cuadre">
          <span className="crece">
            Capital + interés
            {' '}
            <b>{formatearImporte((cuadre.suma / 100).toFixed(2), codigoISO)}</b>
            {cuadre.cuadra ? ' = ' : ' ≠ '}
            lo que pagaste
            {' '}
            <b>{formatearImporte((cuadre.pagado / 100).toFixed(2), codigoISO)}</b>
          </span>
          <span className={`chip ${cuadre.cuadra ? 'ok' : 'e'}`}>{cuadre.cuadra ? 'cuadra' : 'no cuadra'}</span>
        </div>

        {!cuadre.cuadra && cuadre.pagado > 0 && (
          <div className="aviso e" role="status" data-prueba="pago-error-cuadre">
            El capital y el interés tienen que sumar lo que pagaste. Pagaste
            {' '}{formatearImporte((cuadre.pagado / 100).toFixed(2), codigoISO)} y entre los dos suman
            {' '}{formatearImporte((cuadre.suma / 100).toFixed(2), codigoISO)}:
            {' '}{cuadre.diferencia > 0 ? 'faltan' : 'sobran'}
            {' '}{formatearImporte((Math.abs(cuadre.diferencia) / 100).toFixed(2), codigoISO)}.
            {/* D-443 · AJUSTA SOLO AL PULSARLO: la cifra la decide ella, nada se
                ajusta por su cuenta. */}
            <div style={{ marginTop: 8 }}>
              <button
                className="btn"
                type="button"
                onClick={() => setImporteInteres(interesQueCuadra.toFixed(2))}
                data-prueba="pago-poner-el-interes"
              >
                Poner el interés en {formatearImporte(interesQueCuadra.toFixed(2), codigoISO)}
              </button>
            </div>
          </div>
        )}

        <div className="campo">
          <label htmlFor="idCategoriaDelInteres">CATEGORÍA DEL INTERÉS</label>
          <select
            id="idCategoriaDelInteres"
            name="idCategoriaDelInteres"
            value={idCategoriaDelInteres}
            onChange={(evento) => setIdCategoriaDelInteres(evento.target.value)}
            data-prueba="pago-campo-categoria"
          >
            <option value="">Elige la categoría…</option>
            {categorias.map((categoria) => (
              <option key={categoria.idCategoria} value={categoria.idCategoria}>
                {categoria.nombre}
              </option>
            ))}
          </select>
          <span className="ayuda">Hace falta solo si el interés es mayor que cero.</span>

          {faltaLaCategoria && (
            <div className="aviso e" role="status" data-prueba="pago-error-categoria">
              Elige en qué categoría va el interés que pagaste.
            </div>
          )}
        </div>

        <div className="campo">
          <label htmlFor="nota">NOTA (OPCIONAL)</label>
          <input
            id="nota"
            name="nota"
            type="text"
            maxLength={500}
            value={nota}
            onChange={(evento) => setNota(evento.target.value)}
            data-prueba="pago-campo-nota"
          />
        </div>
      </div>

      {/* ===== QUÉ PASA AL GUARDAR ======================================== */}
      {precarga && (
        <div className="card" style={{ margin: 12 }} data-prueba="pago-que-pasa">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>QUÉ PASA AL GUARDAR</h4>
          <span className="ayuda">
            Se crean <b>dos movimientos</b>: un traspaso con el capital, que baja lo que debes de
            {' '}{formatearImporte(precarga.deudaVigente, codigoISO)} a
            {' '}{formatearImporte(
              ((aCentavos(precarga.deudaVigente) - aCentavos(importeCapital)) / 100).toFixed(2),
              codigoISO,
            )}, y un gasto con el interés en el mes de la fecha del pago.
            {' '}<b>El interés no baja tu deuda</b>: es gasto del mes. Las dos cosas se guardan en una
            sola operación: si algo fallara, no quedaría nada a medias.
            {!precarga.planGenerado && (
              <>
                {' '}Como este préstamo todavía no tiene plan, el pago <b>no cubre ningún pago de un
                calendario</b>; si generas el plan después, este pago contará solo en el mes que le
                toca.
              </>
            )}
          </span>
        </div>
      )}

      <div className="card" style={{ margin: 12 }}>
        <button
          className="btn ancho"
          type="button"
          disabled={guardando || !puedeGuardar}
          onClick={guardar}
          data-prueba="pago-registrar"
        >
          Registrar el pago
        </button>

        <button
          className="btn sec ancho"
          type="button"
          onClick={() => navegar(
            vieneDeVencen ? '/cuentas/vencimientos' : `/cuentas/prestamos/${idCuenta}`,
            { replace: true },
          )}
          data-prueba="pago-cancelar"
        >
          Cancelar
        </button>
      </div>
    </Shell>
  );
}
